import { useMsal } from '@azure/msal-react';
import { useRouterState } from '@tanstack/react-router';
import React, { createContext, startTransition, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';

import { analyticsEvents } from '@/contexts/analytics/events';
import { initializeAdobeDataLayer } from '@/contexts/analytics/utils';
import { useFleetAccount } from '@/contexts/fleet-account/useFleetAccount';

interface AnalyticsContextValue {
  analyticsEvents: typeof analyticsEvents;
  isReady: boolean;
}

export const AnalyticsContext = createContext<AnalyticsContextValue | undefined>(undefined);

export const AnalyticsContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { i18n } = useTranslation();
  const { instance } = useMsal();
  const account = instance.getActiveAccount();

  const { userHasPermission, fleetAccountId, fleetAccounts, fleetAccountUnits } = useFleetAccount();

  const [isReady, setIsReady] = useState(false);
  const lastTrackedPathRef = useRef<string>('');
  const { pathname, hash } = useRouterState({ select: (s) => s.location });

  const trackPageView = useCallback(
    async (url: string, title: string, pathname: string) => {
      const userType = account?.username?.match(/@volvo(penta)?\.com$/i) ? 'internal' : 'external';
      const userRole = userHasPermission('dashboard.edit')
        ? 'Administrator'
        : userHasPermission('fleet.read')
          ? 'Fleet Manager'
          : 'User';

      const fleet = fleetAccounts?.find((f) => f.fleetAccountId === fleetAccountId);
      const mainSegment = fleetAccountUnits.map((unit) => unit.mainSegment);
      const unitCount = fleetAccountUnits?.length || 0;
      const claimingCount = fleetAccountUnits?.filter((unit) => unit.claiming)?.length || 0;

      await analyticsEvents.PageView.track({
        url,
        title,
        pathname,
        isMobileContext: isMobile,
        user: {
          email: account?.username,
          type: account?.username ? userType : undefined,
          role: userRole,
          loginStatus: account != null,
          signUpMethod: 'basic',
        },
        fleet: {
          id: fleetAccountId ?? undefined,
          companyId: fleet?.fleetAccountId,
          companyName: fleet?.name,
          size: `${unitCount},${claimingCount},${unitCount - claimingCount}`,
          sizeNum: unitCount,
          primarySegment: mainSegment.every((s) => s === 'Marine')
            ? 'Marine'
            : mainSegment.every((s) => s === 'Industrial')
              ? 'Industrial'
              : 'Marine_Industrial',
        },
        language: i18n.language || 'en',
        country: 'Unknown',
      });
    },
    [account, userHasPermission, fleetAccountId, fleetAccounts, fleetAccountUnits, i18n.language]
  );

  // Track route changes automatically, including the initial page load
  useEffect(() => {
    if (!isReady) return;

    const currentPath = pathname + hash;

    if (currentPath !== lastTrackedPathRef.current) {
      lastTrackedPathRef.current = currentPath;
      trackPageView(window.location.href, document.title, pathname);
    }
  }, [isReady, pathname, hash, trackPageView]);

  useEffect(() => {
    initializeAdobeDataLayer();
    startTransition(() => {
      setIsReady(true);
    });
  }, []);

  const value = useMemo<AnalyticsContextValue>(
    () => ({
      analyticsEvents,
      isReady,
    }),
    [isReady]
  );

  return <AnalyticsContext.Provider value={value}>{children}</AnalyticsContext.Provider>;
};
